import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Minus, Plus, Share2 } from "lucide-react";
import { api, formatDate } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useSite } from "../lib/site";
import ArticleCard from "../components/ArticleCard";
import MediaImg from "../components/MediaImg";
import NewsletterBlock from "../components/NewsletterBlock";
import Skeleton from "../components/Skeleton";

function withHeadingIds(html) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const toc = [];
  doc.querySelectorAll("h2, h3").forEach((h, i) => {
    const id = `s-${(h.textContent || `bagian-${i}`).toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 48)}-${i}`;
    h.id = id;
    toc.push({ id, text: h.textContent, level: h.tagName.toLowerCase() });
  });
  return { html: doc.body.innerHTML, toc };
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSite();
  const [state, setState] = useState({ loading: true, article: null, related: [], redirect: null });
  const [toc, setToc] = useState([]);
  const [progress, setProgress] = useState(0);
  const [fontSize, setFontSize] = useState(() => parseInt(localStorage.getItem("readerSize") || "19", 10));
  const [copied, setCopied] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    setState({ loading: true, article: null, related: [], redirect: null });
    api.get(`/articles/by-slug/${slug}`)
      .then((r) => {
        if (r.data.redirect) {
          setState({ loading: false, redirect: r.data.redirect });
          return;
        }
        const { html, toc } = withHeadingIds(r.data.article.content);
        setState({ loading: false, article: { ...r.data.article, content: html }, related: r.data.related });
        setToc(toc);
      })
      .catch(() => setState({ loading: false, article: null }));
  }, [slug]);

  useSeo({
    title: state.article ? `${state.article.seo_title || state.article.title} — Heri Sensei` : "Tulisan — Heri Sensei",
    description: state.article?.seo_description || state.article?.excerpt || "",
    path: `/tulisan/${slug}`,
    ogType: "article",
  });

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const start = el.offsetTop - 120;
      const total = el.offsetHeight;
      const p = Math.min(1, Math.max(0, (window.scrollY - start) / (total - window.innerHeight + 200)));
      setProgress(p);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [state.article]);

  const changeFont = (delta) => {
    const next = Math.min(24, Math.max(16, fontSize + delta));
    setFontSize(next);
    localStorage.setItem("readerSize", String(next));
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: state.article?.title, url });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (state.loading) {
    return (
      <div className="max-w-[720px] mx-auto px-4 sm:px-8 py-24" aria-busy="true">
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-4 w-1/2 mt-6" />
        <Skeleton className="h-64 mt-10" />
      </div>
    );
  }

  if (state.redirect) {
    navigate(state.redirect, { replace: true });
    return null;
  }

  const a = state.article;
  if (!a) {
    return (
      <div className="max-w-[720px] mx-auto px-4 sm:px-8 py-24 text-center" data-testid="article-not-found">
        <span className="w-2.5 h-2.5 rounded-full bg-enji inline-block mb-6" />
        <h1 className="font-serif text-3xl text-ink">Tulisan tidak ditemukan</h1>
        <p className="text-sub mt-3">Tulisan mungkin dipindahkan atau dihapus.</p>
        <div className="flex gap-3 justify-center mt-8">
          <Link to="/tulisan" className="btn-primary">Lihat semua tulisan</Link>
          <Link to="/" className="btn-ghost">Beranda</Link>
        </div>
      </div>
    );
  }

  return (
    <article data-testid="article-page" className="relative">
      <div className="fixed top-0 left-0 right-0 h-[3px] z-[45] bg-transparent">
        <div className="h-full bg-enji transition-[width] duration-150" style={{ width: `${progress * 100}%` }} data-testid="reading-progress-bar" />
      </div>

      <div className="max-w-[720px] mx-auto px-4 sm:px-8 pt-12 md:pt-16">
        <Link to="/tulisan" className="inline-flex items-center gap-2 text-sm text-sub hover:text-ink transition-colors" data-testid="back-to-articles">
          <ArrowLeft size={15} /> Semua tulisan
        </Link>

        <div className="flex items-center gap-3 text-[12px] uppercase tracking-[0.14em] font-semibold mt-8 flex-wrap">
          {a.category && <span className="text-enji">{a.category}</span>}
          {a.post_type && a.post_type !== "Artikel" && <span className="px-2.5 py-0.5 rounded-full border border-line text-faint normal-case tracking-normal font-medium">{a.post_type}</span>}
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-tight tracking-tight mt-4 text-ink" data-testid="article-title">
          {a.title}
        </h1>
        {a.excerpt && <p className="text-sub text-lg leading-relaxed mt-5">{a.excerpt}</p>}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-faint mt-6 pb-8 border-b border-line">
          <span data-testid="article-author">{settings.brand_name || "Heri Sensei"}</span>
          {a.published_at && <span data-testid="article-date">{formatDate(a.published_at)}</span>}
          {a.updated_at && a.updated_at !== a.published_at && <span>Diperbarui {formatDate(a.updated_at)}</span>}
          <span data-testid="article-reading-time">{a.reading_minutes} menit baca</span>
        </div>
      </div>

      {a.cover_id && (
        <div className="max-w-4xl mx-auto px-4 sm:px-8 mt-10">
          <MediaImg id={a.cover_id} alt={a.cover_alt || a.title} ratio="16/9" className="rounded-xl border border-line" />
        </div>
      )}

      <div className="max-w-[880px] mx-auto px-4 sm:px-8 mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3 order-2 lg:order-1">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="flex items-center gap-2">
              <button onClick={() => changeFont(-1)} data-testid="font-decrease-button" aria-label="Perkecil huruf" className="w-11 h-11 rounded-full border border-line bg-surface flex items-center justify-center text-sub hover:text-ink transition-colors">
                <Minus size={15} />
              </button>
              <button onClick={() => changeFont(1)} data-testid="font-increase-button" aria-label="Perbesar huruf" className="w-11 h-11 rounded-full border border-line bg-surface flex items-center justify-center text-sub hover:text-ink transition-colors">
                <Plus size={15} />
              </button>
              <button onClick={share} data-testid="share-button" aria-label="Bagikan tulisan" className="w-11 h-11 rounded-full border border-line bg-surface flex items-center justify-center text-sub hover:text-ink transition-colors">
                {copied ? <Check size={16} className="text-moss" /> : <Share2 size={15} />}
              </button>
            </div>
            {toc.length > 2 && (
              <nav aria-label="Daftar isi" data-testid="article-toc" className="hidden lg:block border-l border-line pl-4 py-1">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold text-faint mb-3">Daftar Isi</p>
                <ul className="space-y-2">
                  {toc.map((t) => (
                    <li key={t.id} className={t.level === "h3" ? "ml-3" : ""}>
                      <a href={`#${t.id}`} className="text-[13px] leading-snug text-sub hover:text-enji transition-colors block">{t.text}</a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            {toc.length > 2 && (
              <button onClick={() => setTocOpen(!tocOpen)} data-testid="toc-toggle-mobile" className="lg:hidden btn-ghost !min-h-[40px] !px-4 text-sm w-full justify-between">
                Daftar isi {tocOpen ? "▲" : "▼"}
              </button>
            )}
          </div>
        </div>

        <div className="lg:col-span-9 order-1 lg:order-2 min-w-0">
          {toc.length > 2 && tocOpen && (
            <nav aria-label="Daftar isi" className="lg:hidden border border-line bg-surface rounded-lg p-5 mb-8">
              <ul className="space-y-2">
                {toc.map((t) => (
                  <li key={t.id} className={t.level === "h3" ? "ml-3" : ""}>
                    <a href={`#${t.id}`} onClick={() => setTocOpen(false)} className="text-sm text-sub hover:text-enji block">{t.text}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div
            ref={contentRef}
            data-testid="article-content"
            className="reader max-w-[700px]"
            style={{ "--reader-size": `${fontSize}px` }}
            dangerouslySetInnerHTML={{ __html: a.content }}
          />

          {(a.sources || []).length > 0 && (
            <div className="max-w-[700px] mt-12 border-t border-line pt-8" data-testid="article-sources">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-faint mb-4">Sumber &amp; Referensi</p>
              <ol className="space-y-2 list-decimal list-inside">
                {a.sources.map((s, i) => (
                  <li key={i} className="text-[15px]">
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-enji underline underline-offset-2">
                        {s.label || s.url}
                      </a>
                    ) : (
                      <span className="text-sub">{s.label}</span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {a.update_notes && (
            <div className="max-w-[700px] mt-8 bg-surface-muted rounded-lg p-5 text-sm text-sub leading-relaxed" data-testid="article-update-notes">
              <strong className="text-ink">Catatan pembaruan:</strong> {a.update_notes}
            </div>
          )}

          <div className="max-w-[700px] mt-10 flex flex-wrap gap-2">
            {(a.tags || []).map((t) => (
              <Link key={t} to={`/tulisan?tag=${encodeURIComponent(t)}`} data-testid={`article-tag-${t.replace(/\s+/g, "-")}`} className="px-3.5 py-1.5 rounded-full border border-line bg-surface text-[13px] text-sub hover:text-enji hover:border-enji transition-colors">
                #{t}
              </Link>
            ))}
          </div>

          <div className="max-w-[700px] mt-10 bg-surface border border-line rounded-xl p-6 flex items-center gap-5" data-testid="author-card">
            <span className="w-14 h-14 rounded-full bg-enji/10 text-enji font-serif text-xl flex items-center justify-center shrink-0">H</span>
            <div>
              <p className="font-serif text-lg text-ink">{settings.full_name || "Heri Ilham, S.S., M.M."}</p>
              <p className="text-sub text-sm mt-1 leading-relaxed">{settings.tagline || "Penulis Heri Sensei."}</p>
              <Link to="/tentang" className="link-underline text-sm font-semibold text-enji mt-2 inline-block">Tentang penulis</Link>
            </div>
          </div>
        </div>
      </div>

      {state.related.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16 md:py-24">
          <h2 className="font-serif text-3xl tracking-tight text-ink mb-10" data-testid="next-readings-title">Bacaan Selanjutnya</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {state.related.map((r, i) => (
              <ArticleCard key={r.id} article={r} index={i} />
            ))}
          </div>
        </div>
      )}

      <NewsletterBlock />
    </article>
  );
}
